import { ArrowLeft, FolderOpen } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorPage } from '@/components/feedback/state-pages';
import { useDocumentTitle } from '@/hooks/use-document-title';

import { ModuleCard } from '../components/module-card';
import { ModuleIcon } from '../components/module-icon';
import { useModulosDisponibles } from '../hooks/use-modulos-disponibles';

/**
 * Detalle de una categoría: lista los módulos que el usuario puede usar.
 * Selecciona la categoría desde el catálogo ya cacheado por `useModulosDisponibles`.
 */
export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isPending, isError, refetch } = useModulosDisponibles();

  const categoria = data?.categorias.find((item) => item.codigo === slug) ?? null;

  useDocumentTitle(categoria?.nombre ?? 'Módulos');

  if (isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 rounded-card" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-40 rounded-card" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Alert variant="error" title="No se pudo cargar la categoría">
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </Alert>
      </div>
    );
  }

  if (!categoria) {
    return (
      <ErrorPage
        code="404"
        title="Categoría no encontrada"
        description="Esta categoría no existe o tu usuario no tiene permisos sobre sus módulos."
      />
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/inicio"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 transition-colors hover:text-brand-600"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Volver a las categorías
      </Link>

      <header className="flex flex-wrap items-start gap-4 rounded-card border border-ink-100 bg-white p-5 shadow-soft">
        <span
          aria-hidden="true"
          className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-accent-600 text-white"
        >
          <ModuleIcon name={categoria.icono_web} className="size-6" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-ink-900">{categoria.nombre}</h1>
            <Badge variant="brand">
              {categoria.total_modulos} {categoria.total_modulos === 1 ? 'módulo' : 'módulos'}
            </Badge>
          </div>

          <p className="mt-1 text-sm leading-relaxed text-ink-500">
            {categoria.descripcion ?? 'Módulos disponibles de esta categoría.'}
          </p>
        </div>
      </header>

      {categoria.modulos.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {categoria.modulos.map((modulo) => (
            <ModuleCard key={modulo.codigo} modulo={modulo} />
          ))}
        </div>
      ) : (
        <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-12 text-center">
          <FolderOpen className="mx-auto size-6 text-ink-300" aria-hidden="true" />
          <p className="mt-3 text-sm text-ink-500">Esta categoría no tiene módulos habilitados.</p>
        </div>
      )}
    </div>
  );
}
